/**
 * @file schemas.ts
 * @brief Zod schemas for company-scoped invoices (`GET /api/invoices`).
 *
 * @author DES
 * @date 19-06-2026
 */

import { z } from "zod";

const numericString = z.union([
  z.number(),
  z
    .string()
    .transform((v) => (v === "" ? 0 : Number(v)))
    .pipe(z.number()),
]);

// Window presets + the per-currency totals reader now live in
// shared/utils/invoiceTotals so this surface and admin-invoices cannot drift
// (they held byte-identical copies). Re-exported to keep this module's import
// surface unchanged. (24-08-2026)
export {
  invoicePeriodRange,
  resolveInvoiceTotals,
  isMixedCurrency,
} from "@/shared/utils/invoiceTotals";
export type { InvoicePeriod } from "@/shared/utils/invoiceTotals";
import type { InvoicePeriod } from "@/shared/utils/invoiceTotals";

export const CompanyInvoiceRowSchema = z
  .object({
    id: z.string(),
    amount: numericString,
    status: z.string(),
    currency: z.string().nullable().optional(),
    createdAt: z.string().optional(),
    created_at: z.string().nullable().optional(),
    ownerEmail: z.string().nullable().optional(),
    ownerName: z.string().nullable().optional(),
    ownerAvatarUrl: z.string().nullable().optional(),
    billedToCompany: z.boolean().optional(),
    companyName: z.string().nullable().optional(),
    companyId: z.string().nullable().optional(),
    isMine: z.boolean().optional(),
  })
  .passthrough();

export type CompanyInvoiceRow = z.infer<typeof CompanyInvoiceRowSchema>;

export const InvoiceCurrencyTotalSchema = z.object({
  currency: z.string(),
  amountCents: numericString,
  openAmountCents: numericString.optional(),
  count: numericString.optional(),
});

export const CompanyInvoicesResponseSchema = z.object({
  totalAmountCents: numericString.optional(),
  // (24-08-2026) `total` counts every billable row including Plan seats, which
  // is right for an invoice ledger; `usageTotal` excludes them. `openAmountCents`
  // is the SERVER-wide uncaptured sum — never re-derive it from one page.
  usageTotal: numericString.optional(),
  subscriptionAmountCents: numericString.optional(),
  openAmountCents: numericString.optional(),
  totalsByCurrency: z.array(InvoiceCurrencyTotalSchema).optional(),
  total: numericString.optional(),
  page: z.number().optional(),
  pageSize: z.number().optional(),
  items: z.array(CompanyInvoiceRowSchema).default([]),
});

export type CompanyInvoicesResponse = z.infer<
  typeof CompanyInvoicesResponseSchema
>;

export type CompanyInvoicesListParams = {
  page?: number;
  pageSize?: number;
  q?: string;
  from?: string;
  to?: string;
  employee_id?: string;
  period?: InvoicePeriod;
};

export function invoiceCreatedAt(row: CompanyInvoiceRow): string | null {
  return row.createdAt ?? row.created_at ?? null;
}

export function invoiceEmployeeLabel(row: CompanyInvoiceRow): string {
  if (row.ownerName?.trim()) return row.ownerName.trim();
  if (row.ownerEmail) return row.ownerEmail;
  return "—";
}

export function invoiceEmployeeKey(row: CompanyInvoiceRow): string {
  if (row.ownerEmail) return row.ownerEmail.toLowerCase();
  return invoiceEmployeeLabel(row).toLowerCase();
}

export function companyInvoicesPagination(
  data: CompanyInvoicesResponse | undefined,
  params: CompanyInvoicesListParams,
) {
  const pageSize = params.pageSize ?? 25;
  const page = params.page ?? 1;
  const total = Number(data?.total ?? 0);
  return {
    page,
    limit: pageSize,
    total,
    totalPages: Math.max(1, Math.ceil(total / pageSize)),
  };
}
